/**
 * manifest.ts
 *
 * Parses the agent definition manifest (agent.yaml) from a GitAgent directory.
 * This is the sole responsibility of this module — system prompt assembly for
 * SOUL/RULES/knowledge is handled by the renderers at install time and the
 * backend drivers at runtime (native identity).
 */

import fs from "node:fs/promises";
import path from "node:path";
import type { AgentManifest } from "@skaile/workspaces/core";
import { parse as parseYaml } from "yaml";

/** True if the given directory contains a valid GitAgent definition (agent.yaml present). */
/**
 * Check whether an agent definition directory contains a valid agent.yaml.
 *
 * @param agentDir - Path to the agent definition directory
 * @returns true when agent.yaml exists at the given path
 * @docLink packages/runner/dev-guide#manifest-loading
 */
export async function agentDefinitionExists(agentDir: string): Promise<boolean> {
  try {
    await fs.access(path.join(agentDir, "agent.yaml"));
    return true;
  } catch {
    return false;
  }
}

/**
 * Load and parse the agent manifest from `agent.yaml` in the given directory.
 *
 * Returns `undefined` when:
 * - The file does not exist
 * - The file is empty or whitespace-only
 * - The YAML is malformed
 *
 * This replaces the old `buildAgentImprint()` for the manifest-extraction use case.
 * The system prompt assembly that `buildAgentImprint()` used to do is no longer
 * a runtime concern — it is handled by the renderers at install time.
 * @docLink packages/runner/dev-guide#manifest-loading
 */
export async function loadAgentManifest(agentDir: string): Promise<AgentManifest | undefined> {
  const agentYamlPath = path.join(agentDir, "agent.yaml");
  let content: string;
  try {
    content = await fs.readFile(agentYamlPath, "utf-8");
  } catch {
    return undefined;
  }
  if (!content.trim()) return undefined;
  try {
    return parseYaml(content) as AgentManifest;
  } catch {
    return undefined;
  }
}
